import { Injectable } from '@angular/core';
import { paletteColour } from '../shared/palette.utils';

/**
 * Why a capture failed, in terms the reporter can act on.
 *
 * These double as translation keys under `Support_TicketCapture:`, so the
 * widget can surface a specific message rather than "something went wrong" —
 * the difference between a user attaching a file instead and a user giving up.
 */
export type ScreenshotFailureReason =
  | 'captureUnsupported'
  | 'captureBlockedByImage'
  | 'captureEmpty'
  | 'captureFailed';

/** Long enough for any real decode, short enough not to strand the reporter. */
const MEASURE_TIMEOUT_MS = 3000;

export class ScreenshotCaptureError extends Error {
  constructor(
    readonly reason: ScreenshotFailureReason,
    override readonly cause?: unknown,
  ) {
    super(reason);
    this.name = 'ScreenshotCaptureError';
  }
}

/** What a successful capture hands back. Pixels, and their true dimensions. */
export interface IScreenshotCapture {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Turns the page the reporter is looking at into a PNG.
 *
 * Nothing is asked of the browser and no OS dialog appears — which is the
 * whole reason for not using `getDisplayMedia`. A clinician mid-consultation
 * will not grant a screen-share prompt, and a prompt that offers "entire
 * screen" is a prompt that can capture the *other* patient's chart on the
 * second monitor.
 *
 * **On the renderer.** The first implementation used `html-to-image`, which
 * serialises the DOM into an SVG `<foreignObject>` and paints that. On this
 * app it produced an unusable image, for two reasons that are inherent to
 * that technique rather than settings that could be corrected:
 *
 * 1. **`position: fixed` does not survive.** Inside an SVG rendered as an
 *    image there is no viewport for it to resolve against, so the fixed
 *    topbar and the fixed 280px sidebar were dropped, and everything laid out
 *    around them slid left and clipped.
 * 2. **Form values are not in the markup.** A typed or bound value lives in
 *    the element's `value` *property*; serialised markup carries only the
 *    attribute, so every field came out blank — and on a patient form the
 *    values are the entire content of the screenshot.
 *
 * `html2canvas-pro` walks the DOM and paints to a canvas itself, modelling the
 * viewport, scroll offsets and fixed elements explicitly. The second problem
 * belongs to us whatever the renderer, and is handled by `pinFormValues`.
 *
 * The library is loaded on demand: this service is reachable from the capture
 * widget, which is mounted globally in the layout, so a static import would
 * put it in the initial bundle of every page for the majority of sessions
 * that never file a ticket.
 */
@Injectable({ providedIn: 'root' })
export class ScreenshotCaptureService {
  /**
   * Whether this browser can be asked at all.
   *
   * Checked up front so the button can be hidden rather than offered and then
   * failing — the file picker beside it works everywhere.
   */
  isSupported(): boolean {
    return (
      typeof document !== 'undefined' &&
      typeof HTMLCanvasElement !== 'undefined' &&
      typeof HTMLCanvasElement.prototype.toBlob === 'function'
    );
  }

  /**
   * Renders what is currently on screen, minus the nodes in `exclude`.
   *
   * `exclude` is how the widget stays out of its own photograph. Callers hide
   * their own chrome first — a frame is awaited below so that hiding has
   * actually been painted before the DOM is read, otherwise the panel is
   * captured in the act of disappearing.
   */
  async capture(exclude: HTMLElement[] = []): Promise<IScreenshotCapture> {
    if (!this.isSupported()) {
      throw new ScreenshotCaptureError('captureUnsupported');
    }

    await this.nextPaint();

    const { default: html2canvas } = await import('html2canvas-pro');
    const restoreFormValues = this.pinFormValues();

    let canvas: HTMLCanvasElement;
    try {
      // `documentElement`, not `body`: fixed elements resolve against the
      // initial containing block, and handing the renderer the same root the
      // browser uses is what keeps the topbar and the sidebar where they are.
      canvas = await html2canvas(document.documentElement, {
        ignoreElements: (element) =>
          exclude.some((node) => node === element || node.contains(element)),
        // The viewport as the reporter sees it. This app scrolls inside
        // `.layout-body` rather than on the document, so the window's own
        // scroll offsets are zero and the visible region is the viewport box.
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        // A 4K display at the device's own ratio produces a PNG measured in
        // tens of megabytes, against a 5 MB cap. Screenshots are read by a
        // human looking for a wrong number on a form, not zoomed into.
        scale: 1,
        useCORS: true,
        // Never `allowTaint`: a tainted canvas cannot be read back at all, so
        // it would turn a partly-wrong image into no image.
        allowTaint: false,
        // Painted where the page itself is transparent, so it is the app's
        // own paper colour rather than an assumed white.
        backgroundColor: paletteColour('--color-white', '#ffffff'),
        logging: false,
      });
    } catch (error) {
      throw this.classify(error);
    } finally {
      restoreFormValues();
    }

    const blob = await this.encode(canvas);
    if (!blob || blob.size === 0) {
      throw new ScreenshotCaptureError('captureEmpty');
    }

    const { width, height } = await this.measure(blob);
    return { blob, width, height };
  }

  /**
   * Writes each field's current value into its `value` attribute, and returns
   * the undo.
   *
   * A value the user typed, or that Angular bound, lives only in the DOM
   * *property*. Any renderer working from a cloned tree therefore sees an
   * empty field — and a screenshot of a patient form with no values in it
   * tells support nothing at all.
   *
   * Writing the attribute does not change what is displayed: once a control
   * has a dirty value, its attribute is only the default it would reset to.
   * The live DOM is touched rather than the clone because pairing a cloned
   * node back to its original is guesswork the moment any node is excluded,
   * and this is exact. Everything is restored in a `finally`.
   */
  private pinFormValues(): () => void {
    const undo: (() => void)[] = [];

    const remember = (element: Element, attribute: string) => {
      const previous = element.getAttribute(attribute);
      undo.push(() =>
        previous === null
          ? element.removeAttribute(attribute)
          : element.setAttribute(attribute, previous),
      );
    };

    for (const input of Array.from(document.querySelectorAll('input'))) {
      if (input.type === 'checkbox' || input.type === 'radio') {
        remember(input, 'checked');
        input.toggleAttribute('checked', input.checked);
        continue;
      }
      // Passwords are skipped deliberately. This writes into the DOM, however
      // briefly, and a support screenshot has no business carrying one.
      if (input.type === 'password') continue;

      remember(input, 'value');
      input.setAttribute('value', input.value);
    }

    for (const textarea of Array.from(document.querySelectorAll('textarea'))) {
      const previous = textarea.textContent;
      undo.push(() => {
        textarea.textContent = previous;
      });
      textarea.textContent = textarea.value;
    }

    for (const select of Array.from(document.querySelectorAll('select'))) {
      for (const option of Array.from(select.options)) {
        remember(option, 'selected');
        option.toggleAttribute('selected', option.selected);
      }
    }

    return () => {
      for (const restore of undo) restore();
    };
  }

  /**
   * A tainted canvas is the failure worth naming.
   *
   * One image from a host that serves no permissive CORS header poisons the
   * whole canvas, and every read of it throws `SecurityError`. There is
   * nothing the reporter can do about it in the moment, so the message points
   * them at the file picker instead of implying they should retry.
   */
  private classify(error: unknown): ScreenshotCaptureError {
    const name = (error as { name?: string } | undefined)?.name;
    if (name === 'SecurityError') {
      return new ScreenshotCaptureError('captureBlockedByImage', error);
    }
    return new ScreenshotCaptureError('captureFailed', error);
  }

  private encode(canvas: HTMLCanvasElement): Promise<Blob | null> {
    return new Promise((resolve) => {
      try {
        canvas.toBlob((blob) => resolve(blob), 'image/png');
      } catch {
        // Reading back a tainted canvas throws here rather than during the
        // render, so the same failure can arrive from either side.
        resolve(null);
      }
    });
  }

  /**
   * The bitmap's dimensions, for anything that wants to show or check them.
   *
   * Best-effort on purpose. The redactor decodes the blob itself and works
   * from that, so these numbers are informational — losing a good capture
   * because it could not be measured would be a poor trade, and zeroes say
   * "unknown" plainly enough.
   */
  private async measure(blob: Blob): Promise<{ width: number; height: number }> {
    let url: string | null = null;
    try {
      url = URL.createObjectURL(blob);
      const image = new Image();
      const source = url;
      await new Promise<void>((resolve, reject) => {
        // Bounded, because a decoder that neither loads nor errors would
        // otherwise hang the capture the reporter is waiting on. Unknown
        // dimensions cost nothing; a spinner that never stops costs the
        // ticket.
        const timeout = setTimeout(
          () => reject(new Error('Timed out measuring the capture')),
          MEASURE_TIMEOUT_MS,
        );
        image.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        image.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Capture is not a decodable image'));
        };
        image.src = source;
      });
      return { width: image.naturalWidth, height: image.naturalHeight };
    } catch {
      return { width: 0, height: 0 };
    } finally {
      if (url) URL.revokeObjectURL(url);
    }
  }

  /**
   * Two frames, not one: the first lets the caller's signal write flush into
   * a render, the second lets that render reach the screen.
   */
  private nextPaint(): Promise<void> {
    if (typeof requestAnimationFrame !== 'function') {
      return new Promise((resolve) => setTimeout(resolve));
    }
    return new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
  }
}
